import { useMemo, type ReactNode } from 'react';
import { getArticlesByCategory, getCategoryBySlug } from '@/utils/content';
import CategoryHero from '@/pages/category/components/CategoryHero';
import CategoryArticles from '@/pages/category/components/CategoryArticles';
import { breadcrumbSchema, siteUrl, useSeo } from '@/hooks/useSeo';
import NotFound from '@/pages/NotFound';

type CategoryViewProps = {
  categorySlug: string;
  beforeArticles?: ReactNode;
  afterArticles?: ReactNode;
  /** When true the article list is rendered after everything else, at the very bottom. */
  articlesLast?: boolean;
};

export default function CategoryView({
  categorySlug,
  beforeArticles,
  afterArticles,
  articlesLast = false,
}: CategoryViewProps) {
  const category = getCategoryBySlug(categorySlug);
  const articles = getArticlesByCategory(categorySlug);

  const jsonLd = useMemo(() => {
    if (!category) return undefined;
    return [
      breadcrumbSchema([
        { name: 'หน้าแรก', path: '/' },
        { name: category.name, path: `/${category.slug}` },
      ]),
      {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: category.name,
        description: category.description,
        inLanguage: 'th-TH',
        url: siteUrl(`/${category.slug}`),
        hasPart: articles.map((article) => ({
          '@type': 'Article',
          headline: article.title,
          url: siteUrl(`/${article.slug}`),
        })),
      },
    ];
  }, [category, articles]);

  useSeo({
    /* @replace:trading-title */
    title: 'วิธีเทรด Forex | เรียน Forex ภาษาไทยสำหรับมือใหม่',
    description:
      /* @replace:trading-description */
      'เรียนรู้วิธีเทรด Forex ตั้งแต่เลือกคู่เงินและวิเคราะห์ตลาด ไปจนถึงการเลือกประเภทคำสั่ง เปิดออเดอร์ ปิดสถานะ และอ่านต้นทุนการเทรดอย่าง Spread กับสลิปเพจ',
    /* @replace:trading-keywords */
    keywords: 'วิธีเทรด Forex, เปิดออเดอร์ Forex, คำสั่งซื้อขาย, Market Limit Stop, สลิปเพจ, ต้นทุนการเทรด, Forex มือใหม่',
    canonicalPath: `/${categorySlug}`,
    image: category?.image,
    jsonLd,
  });

  if (!category) {
    return <NotFound />;
  }

  return (
    <div className="bg-background-50">
      <CategoryHero category={category} articles={articles} />
      {beforeArticles}
      {articlesLast ? null : <CategoryArticles articles={articles} />}
      {afterArticles}
      {articlesLast ? <CategoryArticles articles={articles} /> : null}
    </div>
  );
}